// App.js

import React, { useState, useRef } from 'react';
import ElementControls from './components/ElementControls';
import PlannerCanvas from './components/PlannerCanvas';
import { useElementHandlers } from './components/useElementHandlers';
import { getDaysInMonth, dayNames } from './components/calendarUtils';

export default function App() {
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [guides, setGuides] = useState([]);
  const [editingText, setEditingText] = useState(null);
  const stageRef = useRef(null);
  const containerRef = useRef(null);

  const {
    handleChange,
    handleTextChange,
    handleTextBlur,
    handleDrag,
    handleDoubleClick,
  } = useElementHandlers(elements, setElements, setGuides, selectedId, setSelectedId, editingText, setEditingText);

  const addElement = (type) => {
    const id = Date.now();
    let newElement;

    switch (type) {
      case 'text':
        newElement = { id, type, x: 50, y: 50, text: 'New Text', fontSize: 16 };
        break;
      case 'checkbox':
        newElement = { id, type, x: 50, y: 50, text: '☐', fontSize: 16 };
        break;
      case 'line':
        newElement = { id, type, x: 50, y: 50, points: [0, 0, 200, 0], stroke: 'black', strokeWidth: 2 };
        break;
      case 'box':
        newElement = { id, type, x: 50, y: 50, width: 100, height: 80, stroke: 'black', strokeWidth: 2 };
        break;
      default:
        return;
    }

    setElements([...elements, newElement]);
  };

  const generateCalendar = () => {
    const original = elements.find((el) => el.id === selectedId && el.type === 'box');
    if (!original) {
      alert('Please select a box to use as template.');
      return;
    }

    const year = parseInt(prompt('Enter year (e.g. 2025):', '2025'), 10);
    const monthOptions = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthPrompt = monthOptions.map((m, i) => `${i + 1}. ${m}`).join("\n");
    const monthInput = parseInt(prompt("Select month:\n" + monthPrompt, '4'), 10);
    const month = monthInput - 1;

    const startDayPrompt = dayNames.map((d, i) => `${i + 1}. ${d}`).join("\n");
    const startDayInput = parseInt(prompt("Select start day of week:\n" + startDayPrompt, '5'), 10);
    const startDay = startDayInput - 1;

    if (isNaN(year) || isNaN(month) || isNaN(startDay)) return;

    const daysInMonth = getDaysInMonth(year, month);
    const spacingX = original.width + 10;
    const spacingY = original.height + 10;
    const firstDay = new Date(year, month, 1).getDay();
    const offset = (firstDay - startDay + 7) % 7;
    const timestampBase = Date.now();

    const newItems = [];

    for (let i = 0; i < 7; i++) {
      const dayName = dayNames[(startDay + i) % 7];
      newItems.push({
        id: timestampBase + i,
        type: 'text',
        x: original.x + i * spacingX + 5,
        y: original.y - 30,
        text: dayName,
        fontSize: 14,
      });
    }

    let dayCounter = 1;
    for (let row = 0; row < 6; row++) {
      for (let col = 0; col < 7; col++) {
        const cellIndex = row * 7 + col;
        const baseX = original.x + col * spacingX;
        const baseY = original.y + row * spacingY;
        const boxId = timestampBase + 1000 + cellIndex;

        if (cellIndex >= offset && dayCounter <= daysInMonth) {
          newItems.push({
            ...original,
            id: boxId,
            x: baseX,
            y: baseY,
          });

          newItems.push({
            id: boxId + 100000,
            type: 'text',
            x: baseX + 5,
            y: baseY + 15,
            text: String(dayCounter),
            fontSize: 12,
          });

          dayCounter++;
        } else {
          newItems.push({
            ...original,
            id: boxId,
            x: baseX,
            y: baseY,
            opacity: 0.1
          });
        }
      }
    }

    setElements([...elements, ...newItems]);
  };

  const selected = elements.find(el => el.id === selectedId);

  return (
    <div style={{ display: 'flex', padding: '1rem' }} ref={containerRef}>
      <ElementControls
        addElement={addElement}
        generateCalendar={generateCalendar}
        selected={selected}
        handleChange={handleChange}
      />

      <PlannerCanvas
        elements={elements}
        guides={guides}
        editingText={editingText}
        handleTextChange={handleTextChange}
        handleTextBlur={handleTextBlur}
        handleDrag={handleDrag}
        handleDoubleClick={handleDoubleClick}
        setSelectedId={setSelectedId}
        stageRef={stageRef}
      />
    </div>
  );
}